// The sentence under the ring count on the statement band.
//
// ⚠️ ITS OWN MODULE, NOT AN EXPORT BESIDE THE COMPONENT — the same reason the
// deleted bandAnchor.ts was its own module: a file that exports both components
// and plain functions breaks React Fast Refresh, and eslint says so at
// --max-warnings=0. It is also a pure function, which is what lets
// statementBandRings.test.ts assert the actual wording by CALLING it rather
// than by grepping the source — the grep it replaced could be satisfied by a
// comment, and for one commit it was.

import type { MarketStatus } from "@/lib/rbCattleTicker";

/** "2026-09-12" -> "Fri, Sep 12". Noon UTC, so a date-only string can never
 *  slip a day when it is parsed as midnight and rendered in a western zone. */
function bandDay(day: string | null | undefined): string | null {
  if (typeof day !== "string" || !day) return null;
  const d = new Date(`${day.slice(0, 10)}T12:00:00Z`);
  return Number.isNaN(d.getTime())
    ? null
    : d.toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
        timeZone: "UTC",
      });
}

/**
 * What the ring count is actually claiming.
 *
 * Three states, and the UNKNOWN one is load-bearing: it is what the prerendered
 * HTML ships and what the browser paints before the effect resolves, so it has
 * to be true whether the ring is running or not. "on this board" is true in
 * every state, and it is already the state board's own closed wording, so the
 * two surfaces speak one vocabulary.
 */
export function ringsSentence(
  rings: string,
  status: MarketStatus | null | undefined,
  day: string | null | undefined,
): string {
  // "barn", not "ring" (owner, 2026-09-13). The count IS a barn count, and
  // "ring" was trade shorthand a first-time visitor had to translate.
  const noun = rings === "1" ? "barn" : "barns";
  if (!status) return `${noun} on this board`;
  const when = bandDay(day);
  // The date rides in BOTH states (owner, 2026-09-13). While the ring is
  // running "now" is the date and this is mild redundancy; closed, it is the
  // whole point — and a line that reads the same every visit is one fewer thing
  // to work out.
  // "sold today", NOT "live now" (2026-09-14 launch audit): the count is every
  // barn that posted a lot today — 53 while only 17 were still selling. "Sold
  // today" is true of all of them; "live now" was not.
  const head = status.open ? `${noun} sold today` : `${noun} reported`;
  return when ? `${head} · ${when}` : head;
}
